import { Card, Flex, SimpleTable, Tabs, Typography } from "djuno-design";
import ExpandableCard from "../components/ExpandableCard";
import { GithubTypesLink } from "./../urls";

const { Title, Text } = Typography;

const TabsPage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Title level={4}>Tabs</Title>
        <Text size="sm" style={{ marginBottom: 30 }}>
          The Tabs component provides a tabbed navigation system, allowing users
          to switch between different content sections.
        </Text>

        <Title level={5}>Variants and Customization</Title>
        <Text size="sm">
          The Tabs component offers the following customization options:
        </Text>
        <ul className="marker:text-sky-400 list-disc pl-6">
          <li>
            <Text size="sm">
              <Text size="sm" strong>
                Options:
              </Text>{" "}
              Configuration object specifying the tabs to display. Each option
              should include a label and content.
            </Text>
          </li>
          <li>
            <Text size="sm">
              <Text size="sm" strong>
                Selected Index:
              </Text>{" "}
              Index of the initially selected tab. Defaults to the first tab.
            </Text>
          </li>
          <li>
            <Text size="sm">
              <Text size="sm" strong>
                On Change:
              </Text>{" "}
              Callback function triggered when the active tab changes.
            </Text>
          </li>

          <li>
            <Text size="sm">
              <Text size="sm" strong>
                List Class Name:
              </Text>{" "}
              Additional CSS classes to apply to the tab list container.
            </Text>
          </li>
          <li>
            <Text size="sm">
              <Text size="sm" strong>
                Panel Class Name:
              </Text>{" "}
              Additional CSS classes to apply to the tab panel container.
            </Text>
          </li>
          <li>
            <Text size="sm">
              <Text size="sm" strong>
                Tab Type:
              </Text>{" "}
              Type of tab style to apply. Options are 'default' and 'creamy'.
            </Text>
          </li>
        </ul>
        <Flex direction="col" className="w-full ">
          <Title level={5} className="mt-4">
            Examples
          </Title>
          <Flex direction="row" className="gap-4">
            {" "}
            <ExpandableCard
              title="Creamy Tabs"
              className="w-full md:w-1/2"
              codeViewerProps={{
                code: TabExample1,
              }}
            >
              <Flex direction="col" className="gap-5 w-full">
                <Tabs
                  listClassName="w-full mb-6"
                  options={[
                    {
                      label: "Djuno Design 1",
                      element: <div>Content for Tab 1</div>,
                    },
                    {
                      label: "Djuno Design 2",
                      element: <div>Content for Tab 2</div>,
                    },
                    {
                      label: "Djuno Design 3",
                      element: <div>Content for Tab 3</div>,
                    },
                  ]}
                  tabType="creamy"
                />
              </Flex>
            </ExpandableCard>
            <ExpandableCard
              title="Default Tabs"
              className="w-full md:w-1/2 "
              codeViewerProps={{
                code: TabExample2,
              }}
            >
              <Flex direction="col" className="gap-5 w-full ">
                <Tabs
                  listClassName="w-full  "
                  options={[
                    {
                      label: "Djuno Design 1",
                      element: <div>Content for Tab 1</div>,
                    },
                    {
                      label: "Djuno Design 2",
                      element: <div>Content for Tab 2</div>,
                    },
                    {
                      label: "Djuno Design 3",
                      element: <div>Content for Tab 3</div>,
                    },
                  ]}
                  tabType="default"
                />
              </Flex>
            </ExpandableCard>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5}>API</Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* options */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">options</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Configuration object specifying the tabs to display.
                        Each option should include a label and content.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "ITab.ts#L45"}
                        size="xs"
                        code
                      >
                        TabOptions
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* selectedIndex */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">selectedIndex</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Index of the initially selected tab. Defaults to the
                        first tab.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        number
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">0</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* onChange */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">onChange</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Callback function triggered when the active tab changes.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link target="_blank" href="" size="xs" code>
                        Function
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* listClassName */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">listClassName</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Additional CSS classes to apply to the tab list
                        container.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* panelClassName */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">panelClassName</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Additional CSS classes to apply to the tab panel
                        container.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* tabType */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">tabType</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Type of tab style to apply. Options are 'default' and
                        'creamy'.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "ITab.ts#L32"}
                        size="xs"
                        code
                      >
                        TabType
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">'default'</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const TabExample1 = `<Flex direction="col" className="gap-5 w-full mt-5">
    <Tabs
      listClassName="w-full  "
      options={[
        {
          label: "Djuno Design 1",
          element: <div>Content for Tab 1</div>,
        },
        {
          label: "Djuno Design 2",
          element: <div>Content for Tab 2</div>,
        },
        {
          label: "Djuno Design 3",
          element: <div>Content for Tab 3</div>,
        },
      ]}
      tabType="default"
    />
  </Flex>`;
const TabExample2 = `<Flex direction="col" className="gap-5 w-full mt-5">
  <Tabs
    listClassName="w-full  "
    options={[
      {
        label: "Djuno Design 1",
        element: <div>Content for Tab 1</div>,
      },
      {
        label: "Djuno Design 2",
        element: <div>Content for Tab 2</div>,
      },
      {
        label: "Djuno Design 3",
        element: <div>Content for Tab 3</div>,
      },
    ]}
    tabType="default"
  />
</Flex>`;

export default TabsPage;
